import { useEffect, useState } from "react";
import { ImageOff } from "lucide-react";
import { useResolvedPhotoUrl } from "@/components/ui-extras";
import type { MoodBoardItem } from "@/components/item-detail-sections";
import { resolvePreferredPhoto } from "@/lib/photo-preference";
import type { CSSProperties } from "react";

// ─── Anchor resolver ────────────────────────────────────────────────────────
function chooseMoodBoardAnchor(items: MoodBoardItem[]): MoodBoardItem | null {
  // When a look has exactly two items and one is clothing, clothing is the anchor.
  // A garment is the visual and practical centre of a simple two-piece look
  // (e.g. a dress + earrings, a blouse + necklace).
  const hasExplicitAnchor =
    items.some((i) => i.role === "anchor") ||
    items.some((i) => i.userSelectedAnchor === true) ||
    items.some((i) => i.source === "eye" && i.intent === "style_this");

  if (!hasExplicitAnchor && items.length === 2) {
    const clothing = items.find((i) => i.type === "clothing" && !!i.photo_url);
    if (clothing) return clothing;
  }

  return (
    items.find((i) => i.role === "anchor") ??
    items.find((i) => i.userSelectedAnchor === true) ??
    items.find((i) => i.source === "eye" && i.intent === "style_this") ??
    items.find((i) => i.type === "jewelry" && !!i.photo_url) ??
    items.find((i) => i.type === "accessory" && !!i.photo_url) ??
    items.find((i) => i.type === "clothing" && !!i.photo_url) ??
    items.find((i) => !!i.photo_url) ??
    items[0] ??
    null
  );
}

function chooseSatellites(items: MoodBoardItem[], anchor: MoodBoardItem): MoodBoardItem[] {
  const pool = items.filter((i) => i.id !== anchor.id);
  const clothing = pool.filter((i) => i.type === "clothing");
  const accessories = pool.filter((i) => i.type === "accessory");
  const jewelry = pool.filter((i) => i.type === "jewelry");
  return [...clothing, ...accessories, ...jewelry].slice(0, 4);
}

const ORBIT_RADIUS = 150;
const CX = 200;
const CY = 200;

function getSatellitePositions(count: number): Array<{ x: number; y: number }> {
  if (count === 0) return [];
  return Array.from({ length: count }, (_, i) => {
    const angleDeg = (count === 4 ? -45 : -90) + (360 / count) * i;
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
      x: Math.round(CX + ORBIT_RADIUS * Math.cos(angleRad)),
      y: Math.round(CY + ORBIT_RADIUS * Math.sin(angleRad)),
    };
  });
}

function OrbitImage({
  src,
  alt,
  missing = false,
  notOwned: _notOwned = false,
  backdrop,
}: {
  src: string | null;
  alt: string;
  missing?: boolean;
  notOwned?: boolean;
  backdrop?: CSSProperties;
}) {
  const { url, refresh } = useResolvedPhotoUrl(src);
  const [retries, setRetries] = useState(0);
  useEffect(() => { setRetries(0); }, [src]);
  const gaveUp = retries >= 2;
  const loading = !missing && !!src && !url && !gaveUp;

  if (missing || !src || gaveUp) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(30, 22, 36, 0.6)",
        }}
        aria-label={alt}
      >
        {missing && <ImageOff size={18} style={{ color: "var(--caileve-lilac)", opacity: 0.5 }} />}
      </div>
    );
  }

  if (loading || !url) {
    return (
      <div
        className="animate-pulse"
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(30, 22, 36, 0.6)",
        }}
        aria-label={alt}
      />
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      onError={() => { setRetries((n) => n + 1); refresh(); }}
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", ...(backdrop ?? {}) }}
    />
  );
}

export type CaileveOrbitBoardProps = {
  items: MoodBoardItem[];
  altName: string;
  compact?: boolean;
  onItemTap?: (item: MoodBoardItem) => void;
};

export function CaileveOrbitBoard({
  items,
  altName,
  compact = false,
  onItemTap,
}: CaileveOrbitBoardProps) {
  useEffect(() => {
    const id = 'caileve-sparkle-keyframes';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @keyframes caileveSparkle {
        0%, 100% { opacity: 0.18; }
        50%       { opacity: 0.45; }
      }
      @media (prefers-reduced-motion: reduce) {
        .caileve-sparkle-halo { animation: none !important; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const anchor = chooseMoodBoardAnchor(items);
  const satellites = anchor ? chooseSatellites(items, anchor) : [];
  const positions = getSatellitePositions(satellites.length);
  const hasOrbit = satellites.length > 0;

  const anchorR = compact ? 80 : 112;
  const satelliteR = compact ? 52 : 65;

  const initial = altName.trim().slice(0, 1).toUpperCase() || "·";

  return (
    <div
      style={{
        background:
          "radial-gradient(ellipse at center, #4A3060 0%, #2E1A42 55%, #1E0F2E 100%)",
        borderRadius: "var(--caileve-radius-md)",
        padding: 16,
        overflow: "hidden",
        width: "100%",
        maxWidth: 440,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "1 / 1",
        }}
      >
        <svg
          viewBox="0 0 400 400"
          width="100%"
          height="100%"
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          aria-hidden="true"
        >
          {hasOrbit && (
            <circle
              cx={CX}
              cy={CY}
              r={ORBIT_RADIUS}
              fill="none"
              stroke="var(--caileve-gold)"
              strokeWidth={1.5}
              strokeDasharray="4 6"
              opacity={0.6}
            />
          )}

          {hasOrbit &&
            positions.map((pos, i) => (
              <line
                key={`line-${i}`}
                x1={CX}
                y1={CY}
                x2={pos.x}
                y2={pos.y}
                stroke="var(--caileve-gold)"
                strokeWidth={0.5}
                opacity={0.35}
              />
            ))}

          {hasOrbit &&
            positions.map((pos, i) => (
              <g key={`sparkle-${i}`}>
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={3}
                  fill="var(--caileve-gold)"
                  opacity={1}
                  filter="drop-shadow(0 0 4px rgba(198,166,107,0.8))"
                />
                <circle
                  className="caileve-sparkle-halo"
                  cx={pos.x}
                  cy={pos.y}
                  r={5.5}
                  fill="var(--caileve-gold)"
                  style={{
                    animationDuration: `${3 + i * 0.7}s`,
                    animationTimingFunction: "ease-in-out",
                    animationIterationCount: "infinite",
                    animationName: "caileveSparkle",
                  }}
                />
              </g>
            ))}

          <circle
            cx={CX}
            cy={CY}
            r={anchorR}
            fill="none"
            stroke="var(--caileve-gold)"
            strokeWidth={2}
            opacity={1}
          />

          <circle
            cx={CX}
            cy={CY}
            r={anchorR - 18}
            fill="none"
            stroke="var(--caileve-gold)"
            strokeWidth={0.75}
            opacity={0.3}
          />

          {satellites.map((sat, i) => (
            sat.photo_url ? (
              <circle
                key={`sat-ring-${i}`}
                cx={positions[i].x}
                cy={positions[i].y}
                r={satelliteR}
                fill="rgba(30, 22, 36, 0.7)"
                stroke="var(--caileve-gold)"
                strokeWidth={1.5}
                opacity={0.85}
              />
            ) : null
          ))}
        </svg>

        {anchor && (() => {
          const resolved = resolvePreferredPhoto(anchor);
          if (!resolved.src) return null;
          const sizePct = (anchorR * 2 / 400) * 100;
          const offsetPct = 50 - sizePct / 2;
          return (
            <div
              style={{
                position: "absolute",
                left: `${offsetPct}%`,
                top: `${offsetPct}%`,
                width: `${sizePct}%`,
                height: `${sizePct}%`,
                borderRadius: "50%",
                overflow: "hidden",
                background: "radial-gradient(circle at center, rgba(61,32,80,0.92) 0%, rgba(30,22,36,0.97) 100%)",
              }}
            >
              <OrbitImage
                src={resolved.src}
                alt={anchor.name}
                missing={anchor.missing}
                notOwned={anchor.notOwned}
                backdrop={resolved.backdropStyle}
              />
            </div>
          );
        })()}

        {anchor && !resolvePreferredPhoto(anchor).src && !anchor.missing && (() => {
          const sizePct = (anchorR * 2 / 400) * 100;
          const offsetPct = 50 - sizePct / 2;
          return (
            <div
              style={{
                position: "absolute",
                left: `${offsetPct}%`,
                top: `${offsetPct}%`,
                width: `${sizePct}%`,
                height: `${sizePct}%`,
                borderRadius: "50%",
                background: "radial-gradient(circle at center, rgba(61,32,80,0.92) 0%, rgba(30,22,36,0.97) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: 44,
                  color: "var(--caileve-lilac)",
                }}
              >
                {initial}
              </span>
            </div>
          );
        })()}

        {satellites.map((sat, i) => {
          const pos = positions[i];
          const sizePct = (satelliteR * 2 / 400) * 100;
          const leftPct = (pos.x / 400) * 100 - sizePct / 2;
          const topPct = (pos.y / 400) * 100 - sizePct / 2;
          const resolved = resolvePreferredPhoto(sat);
          return (
            <button
              key={sat.id}
              type="button"
              onClick={onItemTap ? () => onItemTap(sat) : undefined}
              style={{
                position: "absolute",
                left: `${leftPct}%`,
                top: `${topPct}%`,
                width: `${sizePct}%`,
                height: `${sizePct}%`,
                borderRadius: "50%",
                overflow: "hidden",
                background: "radial-gradient(circle at center, rgba(61,32,80,0.92) 0%, rgba(30,22,36,0.97) 100%)",
                border: "none",
                cursor: onItemTap ? "pointer" : "default",
              }}
              aria-label={sat.name}
            >
              <OrbitImage
                src={resolved.src}
                alt={sat.name}
                missing={sat.missing}
                notOwned={sat.notOwned}
                backdrop={resolved.backdropStyle}
              />
            </button>
          );
        })}

        {!anchor && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: 44,
                color: "var(--caileve-lilac)",
              }}
            >
              {initial}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
